import './globalStyle.scss';
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import * as Pages from './pages';
import { launchToast, ToastNotification } from './utils';
import { MainLayout } from './layouts';
import { useAppContext } from './context';
import { LOCALSTORAGE_TOKEN_LABEL } from './constants';
import { AUTH_API } from './api-handle/modules/auth.api';
import { ANNOTATOR_API } from './api-handle';

/**
 * App component is the root component of the application.
 * It contains the routing and the main layout.
 * It also fetches the dynamic data that stays the same throughout the application,
 * so that we don't have to fetch it every time we navigate to a new page.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function App() {
  const {
    user,
    setUser,
    setSiteLoadingEnabled,
    setAllDevices,
    setAllUsers,
    setAllEvents,
    setLoadingGlobalAssets,
  } = useAppContext();

  //global loaders for global assets
  const [loadingAllDevices, setLoadingAllDevices] = useState(true);
  const [loadingAllUsers, setLoadingAllUsers] = useState(true);
  const [loadingAllEvents, setLoadingAllEvents] = useState(true);

  useEffect(() => {
    if (!user) {
      (async () => {
        try {
          let tok = window.localStorage.getItem(LOCALSTORAGE_TOKEN_LABEL);
          if (tok) {
            let res = await AUTH_API.checkSession(tok);
            if (res && res.status && res.data) {
              setUser(res.data);
            } else {
              setUser(null);
            }
          }
          setSiteLoadingEnabled(false);
        } catch (e) {
          launchToast.error('Invalid Credentials!');
          setSiteLoadingEnabled(false);
        }
      })();
    }
    // eslint-disable-next-line
  }, []);

  // fetch and set all devices data to be used globally for optimization and performance
  useEffect(() => {
    if (user) {
      ANNOTATOR_API.getAllDevices(
        window.localStorage.getItem(LOCALSTORAGE_TOKEN_LABEL),
        1,
        1,
        0
      )
        .then((res) => {
          setAllDevices(res.data.data);
          setLoadingAllDevices(false);
        })
        .catch((err) => {
          console.log(err);
          launchToast.error('Unable to load data from server!');
          setLoadingAllDevices(false);
        });
    }
    // eslint-disable-next-line
  }, [user]);
  // fetch and set all events data to be used globally for optimization and performance
  useEffect(() => {
    if (user) {
      ANNOTATOR_API.getAllEvents(
        window.localStorage.getItem(LOCALSTORAGE_TOKEN_LABEL),
        {},
        1,
        1,
        0
      )
        .then((res) => {
          setAllEvents(res.data.data);
          setLoadingAllEvents(false);
        })
        .catch((err) => {
          console.log(err);
          launchToast.error('Unable to load data from server!');
          setLoadingAllEvents(false);
        });
    }
    // eslint-disable-next-line
  }, [user]);
  // fetch and set all users data to be used globally for optimization and performance
  useEffect(() => {
    if (user) {
      ANNOTATOR_API.getAllUsers(
        window.localStorage.getItem(LOCALSTORAGE_TOKEN_LABEL),
        1,
        1,
        0
      )
        .then((res) => {
          setAllUsers(res.data.data);
          setLoadingAllUsers(false);
        })
        .catch((err) => {
          console.log(err);
          launchToast.error('Unable to load data from server!');
          setLoadingAllUsers(false);
        });
    }
    // eslint-disable-next-line
  }, [user]);
  useEffect(() => {
    if (!loadingAllDevices && !loadingAllUsers && !loadingAllEvents) {
      setLoadingGlobalAssets(false);
    }
    // eslint-disable-next-line
  }, [loadingAllDevices, loadingAllUsers, loadingAllEvents]);

  return (
    <div className="App">
      <ToastNotification />
      <BrowserRouter>
        <Routes>
          {/* statistics*/}
          <Route
            path="/"
            element={
              <MainLayout isForAuth>
                <Pages.Statistics />
              </MainLayout>
            }
          />

          {/* annotations */}
          <Route
            path="/annotations"
            element={
              <MainLayout isForAuth>
                <Pages.Annotations />
              </MainLayout>
            }
          />

          {/* events */}
          <Route
            path="/events"
            element={
              <MainLayout isForAuth>
                <Pages.Events />
              </MainLayout>
            }
          />

          {/* event details*/}
          <Route
            path="/event-details"
            element={
              <MainLayout isForAuth>
                <Pages.EventDetails />
              </MainLayout>
            }
          />

          {/* login */}
          <Route
            path="/login"
            element={
              <MainLayout>
                <Pages.Login />
              </MainLayout>
            }
          />

          {/* forgot password */}
          <Route
            path="/forgot-password"
            element={
              <MainLayout>
                <Pages.ForgotPassword />
              </MainLayout>
            }
          />

          {/* confirm reset password */}
          <Route
            path="/confirm-reset-password"
            element={
              <MainLayout>
                <Pages.ConfirmResetPassword />
              </MainLayout>
            }
          />

          {/* not found page */}
          <Route
            path="*"
            element={
              <MainLayout isForAuth>
                <Pages.NotFoundPage />
              </MainLayout>
            }
          />

          {/* not found page */}
          <Route
            path="/404"
            element={
              <MainLayout isForAuth>
                <Pages.NotFoundPage />
              </MainLayout>
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
